import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Building2, Pencil, Trash2, X, Save } from 'lucide-react';
import { getCliente, updateCliente, deleteCliente } from '../api/clienti';
import type { ClienteUpdatePayload } from '../api/clienti';
import type { Cliente } from '../types/cliente';
import { clienteUpdateSchema, type ClienteUpdateData } from '../schemas/cliente';
import { getRichieste, type RichiestaListItem } from '../api/richieste';
import { useAuthStore } from '../store/authStore';
import { useCategorie } from '../hooks/useCategorie';
import { categoriaLabel } from '../lib/categorie';
import { Skeleton, SkeletonRow } from '../components/ui/Skeleton';
import StatusBadge from '../components/ui/StatusBadge';
import ConfermaModificaFiscaleModal from '../components/ConfermaModificaFiscaleModal';
import AvvisoBozzeAperte from '../components/AvvisoBozzeAperte';
import AvvisoOmonimiaCf from '../components/AvvisoOmonimiaCf';
import { apiErrorMessage } from '../lib/apiError';
import {
  detailTesto,
  parseConfermaFiscale,
  type ConfermaModificaFiscaleDetail,
  type FatturaRiferimento,
} from '../lib/modificaFiscale';
import { canWriteAnagraficaCommerciale } from '../lib/constants';

function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-2.5 border-b border-stone-mid last:border-0 items-start">
      <dt className="text-xs text-ink-soft min-w-[160px] pt-0.5 flex-shrink-0">{label}</dt>
      <dd className="flex-1 text-sm text-ink-dark">{children}</dd>
    </div>
  );
}

function FieldWrap({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-1.5 border-b border-stone-mid last:border-0 items-start">
      <label className="text-xs text-ink-soft min-w-[160px] pt-2 flex-shrink-0">{label}</label>
      <div className="flex-1">
        {children}
        {error && <p className="text-xs text-accent-danger mt-0.5">{error}</p>}
      </div>
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="p-8 max-w-5xl mx-auto animate-pulse">
      <Skeleton className="h-4 w-32 mb-6" />
      <div className="flex gap-6">
        <div className="flex-[3] space-y-4">
          <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="px-5 py-4 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-4 w-full" />)}
            </div>
          </div>
        </div>
        <div className="flex-[1] space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    </div>
  );
}

export default function DettaglioClientePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { categorie } = useCategorie();   // R4-A: lookup codice → nome_visualizzato
  const { user } = useAuthStore();

  const userRole = user?.role ?? '';
  const canEdit = canWriteAnagraficaCommerciale(userRole);

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Guardia modifica dati fiscali (409): detail del backend + payload da
  // rinviare con `conferma_modifica_fiscale` una volta confermato.
  const [confermaFiscale, setConfermaFiscale] = useState<ConfermaModificaFiscaleDetail | null>(null);
  const [payloadInSospeso, setPayloadInSospeso] = useState<ClienteUpdatePayload | null>(null);
  const [avvisoBozze, setAvvisoBozze] = useState<FatturaRiferimento[]>([]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [ordini, setOrdini] = useState<RichiestaListItem[]>([]);
  const [ordiniLoading, setOrdiniLoading] = useState(false);

  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm<ClienteUpdateData>({
    resolver: zodResolver(clienteUpdateSchema),
  });

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      try {
        setCliente(await getCliente(id));
      } catch (err) {
        if ((err as { response?: { status?: number } })?.response?.status === 404) setNotFound(true);
        else setFetchError(t('anagrafiche.dett_cliente.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setOrdiniLoading(true);
      try {
        setOrdini(await getRichieste({ customer_id: id }));
      } catch {
        setOrdini([]);
      } finally {
        setOrdiniLoading(false);
      }
    })();
  }, [id]);

  function startEdit() {
    if (!cliente) return;
    reset({
      ragione_sociale: cliente.ragione_sociale,
      piva: cliente.piva ?? '',
      codice_fiscale: cliente.codice_fiscale ?? '',
      indirizzo: cliente.indirizzo ?? '',
      cap: cliente.cap ?? '',
      citta: cliente.citta ?? '',
      provincia: cliente.provincia ?? '',
      nazione: cliente.nazione ?? 'IT',
      codice_destinatario: cliente.codice_destinatario ?? '0000000',
      referente: cliente.referente ?? '',
      telefono: cliente.telefono ?? '',
      email: cliente.email ?? '',
      pec: cliente.pec ?? '',
    });
    setIsEditing(true);
    setSaveError(null);
  }

  function cancelEdit() {
    setIsEditing(false);
    setSaveError(null);
  }

  /**
   * Il backend usa `exclude_unset`: chiave ASSENTE = campo invariato, chiave a
   * `null` = cancellazione esplicita. I 9 campi annullabili vanno quindi
   * inviati SEMPRE, anche se invariati, altrimenti svuotarli è inesprimibile.
   * `ragione_sociale`, `email`, `nazione` e `codice_destinatario` sono NOT NULL
   * a schema: si inviano solo se valorizzati (un null darebbe 422).
   */
  function buildPayload(data: ClienteUpdateData): ClienteUpdatePayload {
    const p: ClienteUpdatePayload = {};
    if (data.ragione_sociale) p.ragione_sociale = data.ragione_sociale;
    p.piva = data.piva?.trim() || null;
    p.codice_fiscale = data.codice_fiscale?.trim() || null;
    p.indirizzo = data.indirizzo?.trim() || null;
    p.cap = data.cap?.trim() || null;
    p.citta = data.citta?.trim() || null;
    p.provincia = data.provincia?.trim() || null;
    if (data.nazione?.trim()) p.nazione = data.nazione.trim();
    if (data.codice_destinatario?.trim()) p.codice_destinatario = data.codice_destinatario.trim();
    p.referente = data.referente?.trim() || null;
    p.telefono = data.telefono?.trim() || null;
    if (data.email?.trim()) p.email = data.email.trim();
    p.pec = data.pec?.trim() || null;
    return p;
  }

  async function salva(payload: ClienteUpdatePayload) {
    if (!id) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await updateCliente(id, payload);
      setCliente(updated);
      setIsEditing(false);
      setConfermaFiscale(null);
      setPayloadInSospeso(null);
      setAvvisoBozze(updated.avviso_bozze_aperte ?? []);
    } catch (err: unknown) {
      // 409 strutturato = serve conferma esplicita: si apre la modale e si
      // tiene da parte il payload per rinviarlo con il flag di conferma.
      const conferma = parseConfermaFiscale(err);
      if (conferma) {
        setConfermaFiscale(conferma);
        setPayloadInSospeso(payload);
        return;
      }
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detail = detailTesto(err);
      if (status === 409) {
        if (detail?.includes('P.IVA') || detail?.toLowerCase().includes('piva')) {
          setError('piva', { message: detail ?? t('anagrafiche.crea_cliente.piva_esistente') });
        } else {
          setSaveError(detail ?? t('anagrafiche.dett_cliente.conflitto'));
        }
      } else if (status === 422) {
        // `detailTesto` copre solo il detail stringa: il 422 di validazione
        // Pydantic (es. checksum del CF) e' un ARRAY {loc,msg} e finiva nel
        // fallback generico. `apiErrorMessage` gestisce entrambe le forme.
        setSaveError(apiErrorMessage(err, t('anagrafiche.crea_cliente.errore_salvataggio')));
      } else {
        setSaveError(t('anagrafiche.crea_cliente.errore_salvataggio'));
      }
    } finally {
      setIsSaving(false);
    }
  }

  const onSubmit = async (data: ClienteUpdateData) => {
    setAvvisoBozze([]);
    await salva(buildPayload(data));
  };

  async function onConfermaFiscale() {
    if (!payloadInSospeso) return;
    await salva({ ...payloadInSospeso, conferma_modifica_fiscale: true });
  }

  function annullaConfermaFiscale() {
    setConfermaFiscale(null);
    setPayloadInSospeso(null);
  }

  async function handleDelete() {
    if (!id) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteCliente(id);
      navigate('/clienti');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (status === 409) {
        setDeleteError(detail ?? t('anagrafiche.dett_cliente.in_uso'));
      } else {
        setDeleteError(t('anagrafiche.dett_cliente.errore_cancellazione'));
      }
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  }

  if (loading) return <PageSkeleton />;

  if (notFound) {
    return (
      <div className="p-8 text-center">
        <p className="text-2xl font-bold text-ink-dark mb-2">404</p>
        <p className="text-ink-soft mb-4">{t('anagrafiche.dett_cliente.non_trovato')}</p>
        <Link to="/clienti" className="text-sage underline text-sm">{t('anagrafiche.dett_cliente.torna')}</Link>
      </div>
    );
  }

  if (fetchError || !cliente) {
    return (
      <div className="p-8 text-center">
        <p className="text-accent-danger font-medium mb-4">{fetchError ?? t('anagrafiche.dett_cliente.errore_sconosciuto')}</p>
        <button type="button" onClick={() => navigate('/clienti')} className="text-sage underline text-sm">
          {t('anagrafiche.dett_cliente.torna')}
        </button>
      </div>
    );
  }

  const c = cliente;

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 text-sm mb-6">
          <Link to="/clienti" className="text-sage hover:underline flex items-center gap-1">
            <ArrowLeft size={14} />
            {t('anagrafiche.clienti.titolo')}
          </Link>
          <span className="text-ink-faint">/</span>
          <span className="font-semibold text-ink-dark">{c.ragione_sociale}</span>
        </div>

        {avvisoBozze.length > 0 && (
          <div className="mb-4">
            <AvvisoBozzeAperte bozze={avvisoBozze} onDismiss={() => setAvvisoBozze([])} />
          </div>
        )}

        {/* V67 — omonimia CF: persistente, non chiudibile, e' uno stato del dato. */}
        {(cliente?.avviso_omonimia_cf?.length ?? 0) > 0 && (
          <div className="mb-4">
            <AvvisoOmonimiaCf omonimi={cliente!.avviso_omonimia_cf!} />
          </div>
        )}

        <div className="flex gap-6 items-start">
          <div className="flex-[3] min-w-0 space-y-4">

            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 size={16} className="text-ink-soft" />
                  <span className="text-sm font-semibold text-ink-dark">{t('anagrafiche.dett_cliente.dati_cliente')}</span>
                </div>
                {canEdit && !isEditing && (
                  <button type="button" onClick={startEdit} data-testid="btn-modifica-cliente"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors">
                    <Pencil size={12} />
                    {t('anagrafiche.dett_cliente.modifica')}
                  </button>
                )}
                {isEditing && (
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={cancelEdit} disabled={isSaving}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors">
                      <X size={12} />
                      {t('common.azioni.annulla')}
                    </button>
                    <button type="button" onClick={handleSubmit(onSubmit)} disabled={isSaving} data-testid="btn-salva-cliente"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark transition-colors disabled:opacity-60">
                      <Save size={12} />
                      {isSaving ? t('anagrafiche.dett_cliente.salvataggio') : t('anagrafiche.dett_cliente.salva')}
                    </button>
                  </div>
                )}
              </div>

              <div className="px-5 py-4">
                {saveError && (
                  <div className="mb-3 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-sm text-accent-danger">
                    {saveError}
                  </div>
                )}

                {isEditing ? (
                  <dl className="divide-y divide-stone-mid">
                    <FieldWrap label={t('anagrafiche.campi.ragione_sociale')} error={errors.ragione_sociale?.message}>
                      <input className={inputCls} {...register('ragione_sociale')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.partita_iva')} error={errors.piva?.message}>
                      <input className={inputCls} data-testid="input-cliente-piva" {...register('piva')} maxLength={11} placeholder={t('anagrafiche.crea_cliente.ph_piva')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.codice_fiscale')} error={errors.codice_fiscale?.message}>
                      <input className={inputCls} {...register('codice_fiscale')} maxLength={16} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.indirizzo')} error={errors.indirizzo?.message}>
                      <input className={inputCls} {...register('indirizzo')} placeholder={t('anagrafiche.crea_cliente.ph_indirizzo')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.cap')} error={errors.cap?.message}>
                      <input className={inputCls} {...register('cap')} maxLength={5} placeholder={t('anagrafiche.crea_cliente.ph_cap')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.comune')} error={errors.citta?.message}>
                      <input className={inputCls} {...register('citta')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.provincia')} error={errors.provincia?.message}>
                      <input className={inputCls} {...register('provincia')} maxLength={2} placeholder={t('anagrafiche.crea_cliente.ph_provincia')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.nazione')} error={errors.nazione?.message}>
                      <input className={inputCls} {...register('nazione')} maxLength={2} placeholder="IT" />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.codice_destinatario')} error={errors.codice_destinatario?.message}>
                      <input className={inputCls} {...register('codice_destinatario')} maxLength={7} placeholder={t('anagrafiche.crea_cliente.ph_codice_dest')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.referente')} error={errors.referente?.message}>
                      <input className={inputCls} {...register('referente')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.telefono')} error={errors.telefono?.message}>
                      <input className={inputCls} {...register('telefono')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.email')} error={errors.email?.message}>
                      <input className={inputCls} type="email" {...register('email')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.pec')} error={errors.pec?.message}>
                      <input className={inputCls} type="email" {...register('pec')} />
                    </FieldWrap>
                  </dl>
                ) : (
                  <dl className="divide-y divide-stone-mid">
                    <InfoRow label={t('anagrafiche.campi.ragione_sociale')}>
                      <span className="font-medium">{c.ragione_sociale}</span>
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.partita_iva')}>
                      <span data-testid="valore-cliente-piva">
                        {c.piva ? <span className="font-mono">{c.piva}</span> : <span className="text-ink-faint">—</span>}
                      </span>
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.codice_fiscale')}>
                      {c.codice_fiscale ? <span className="font-mono">{c.codice_fiscale}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.indirizzo')}>
                      {c.indirizzo ? <span>{c.indirizzo}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.dett_cliente.cap_comune_prov')}>
                      {(c.cap || c.citta || c.provincia)
                        ? <span>{[c.cap, c.citta, c.provincia && `(${c.provincia})`].filter(Boolean).join(' ')}</span>
                        : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.nazione')}>
                      {c.nazione ? <span>{c.nazione}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.codice_destinatario')}>
                      {c.codice_destinatario ? <span className="font-mono">{c.codice_destinatario}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.referente')}>
                      {c.referente ? <span>{c.referente}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.telefono')}>
                      {c.telefono ? (
                        <a href={`tel:${c.telefono}`} className="text-sage hover:underline">{c.telefono}</a>
                      ) : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.email')}>
                      {c.email ? (
                        <a href={`mailto:${c.email}`} className="text-sage hover:underline">{c.email}</a>
                      ) : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.campi.pec')}>
                      {c.pec ? <span>{c.pec}</span> : <span className="text-ink-faint">—</span>}
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.dett_cliente.creato_il')}>
                      <span className="text-ink-soft">{formatDateIT(c.created_at)}</span>
                    </InfoRow>
                    <InfoRow label={t('anagrafiche.dett_cliente.aggiornato_il')}>
                      <span className="text-ink-soft">{formatDateIT(c.updated_at)}</span>
                    </InfoRow>
                  </dl>
                )}
              </div>
            </div>

            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
                <span className="text-sm font-semibold text-ink-dark">
                  {t('anagrafiche.dett_cliente.ordini_collegati')} {!ordiniLoading && `(${ordini.length})`}
                </span>
              </div>
              <div className="divide-y divide-stone-mid">
                {ordiniLoading ? (
                  <div className="px-5 py-3 space-y-2">
                    {[1, 2].map((i) => <SkeletonRow key={i} />)}
                  </div>
                ) : ordini.length === 0 ? (
                  <div className="px-5 py-4 text-sm text-ink-soft italic">{t('anagrafiche.dett_cliente.nessun_ordine')}</div>
                ) : (
                  ordini.map((o) => (
                    <div
                      key={o.id}
                      onClick={() => navigate(`/richieste/${o.id}`)}
                      className="flex items-center justify-between px-5 py-3 hover:bg-stone-bg transition-colors cursor-pointer"
                    >
                      <div>
                        <p className="text-sm font-mono font-semibold text-ink-dark">{o.codice_ordine}</p>
                        <p className="text-xs text-ink-soft">{categoriaLabel(o.categoria, categorie)} · {o.modalita_conferimento}</p>
                      </div>
                      <StatusBadge status={o.status} />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex-[1] min-w-[200px] space-y-3">
            {canEdit && !isEditing && (
              <button type="button" onClick={startEdit}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors">
                <Pencil size={14} />
                {t('anagrafiche.dett_cliente.modifica')}
              </button>
            )}

            {canEdit && (
              <>
                {!showDeleteConfirm ? (
                  <button type="button" onClick={() => { setDeleteError(null); setShowDeleteConfirm(true); }} disabled={isEditing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-accent-danger border border-[#FECACA] rounded-lg hover:bg-accent-danger-bg transition-colors disabled:opacity-40">
                    <Trash2 size={14} />
                    {t('anagrafiche.dett_cliente.elimina')}
                  </button>
                ) : (
                  <div className="border border-[#FECACA] rounded-lg p-4 bg-accent-danger-bg">
                    <p className="text-sm text-ink-dark font-medium mb-1">{t('anagrafiche.dett_cliente.conferma_elimina')}</p>
                    <p className="text-xs text-ink-soft mb-3">{t('anagrafiche.dett_cliente.conferma_elimina_nota')}</p>
                    {deleteError && <p className="text-xs text-accent-danger mb-2">{deleteError}</p>}
                    <div className="flex gap-2">
                      <button type="button" onClick={handleDelete} disabled={isDeleting}
                        className="flex-1 px-3 py-2 text-xs font-medium text-white bg-accent-danger rounded-md hover:bg-[#B91C1C] transition-colors disabled:opacity-60">
                        {isDeleting ? t('anagrafiche.dett_cliente.eliminazione') : t('anagrafiche.dett_cliente.si_elimina')}
                      </button>
                      <button type="button" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}
                        className="flex-1 px-3 py-2 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors">
                        {t('common.azioni.annulla')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            <button type="button" onClick={() => navigate('/clienti')}
              className="w-full px-4 py-2 text-sm text-ink-soft border border-stone-border rounded-lg hover:bg-stone-bg transition-colors">
              {t('anagrafiche.dett_cliente.torna')}
            </button>
          </div>
        </div>
      </div>

      {confermaFiscale && (
        <ConfermaModificaFiscaleModal
          detail={confermaFiscale}
          isSaving={isSaving}
          onConfirm={() => { void onConfermaFiscale(); }}
          onCancel={annullaConfermaFiscale}
        />
      )}
    </div>
  );
}
